"use client";

/**
 * useReferralAttribution — reads post-author referral context from URL
 * and exposes a stable snapshot for the current page view.
 *
 * URL params: `?via=<author-uuid>&p=<post-uuid>`. Names intentionally
 * avoid `ref_*` because X/Twitter and some chat clients strip or
 * shorten URLs that look like tracking links — `via` (already used by
 * Twitter for citation) and `p` (post id) read as content metadata,
 * not affiliate tracking, and survive the share path intact.
 *
 * Flow:
 *   1. User clicks `/market/slug?via=<uuid>&p=<uuid>` from a Frenflow
 *      share link.
 *   2. This hook snapshots the params on mount. Subsequent navigation
 *      does NOT re-read — attribution is "single visit, single session".
 *   3. Consumer (TradingPanel / TradingModal) passes the snapshot into
 *      the trade submit body. Backend credits the post author 20% of
 *      the net builder fee after anti-abuse checks.
 *
 * Fingerprint: a lightweight hash of user-agent + timezone + language +
 * screen dims. Enough for the backend to detect the "same browser" case
 * when comparing against the author's last stored fingerprint. We do
 * NOT use a heavy library (FingerprintJS) because we only need coarse
 * device equality, not uniqueness across the whole web.
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";

export interface ReferralAttribution {
  postId?: string;
  authorId?: string;
  fingerprint?: string;
}

/** Stable hash of coarse browser signals. Not unique — just "same-browser" */
function computeBrowserFingerprint(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const parts = [
      navigator.userAgent,
      navigator.language,
      Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      `${window.screen.width}x${window.screen.height}`,
      `${window.screen.colorDepth}`,
      `${new Date().getTimezoneOffset()}`,
    ];
    const raw = parts.join("|");
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      hash = (hash * 31 + raw.charCodeAt(i)) | 0;
    }
    // Short readable hash (unsigned hex)
    return `fp_${(hash >>> 0).toString(16)}`;
  } catch {
    return undefined;
  }
}

/** UUIDv4 loose check — backend still validates, this just rejects junk */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useReferralAttribution(
  /**
   * When provided, these override URL-derived values. Used by call sites
   * that pass attribution directly (Activity feed post card → modal open)
   * instead of going through navigation.
   */
  override?: { postId?: string | null; authorId?: string | null },
): ReferralAttribution {
  const searchParams = useSearchParams();
  const [fingerprint, setFingerprint] = useState<string | undefined>(undefined);

  useEffect(() => {
    setFingerprint(computeBrowserFingerprint());
  }, []);

  return useMemo<ReferralAttribution>(() => {
    const overridePost = override?.postId && UUID_RE.test(override.postId) ? override.postId : undefined;
    const overrideAuthor = override?.authorId && UUID_RE.test(override.authorId) ? override.authorId : undefined;

    if (overridePost && overrideAuthor) {
      return { postId: overridePost, authorId: overrideAuthor, fingerprint };
    }

    const urlPost = searchParams?.get("p") || undefined;
    const urlAuthor = searchParams?.get("via") || undefined;

    const postId = urlPost && UUID_RE.test(urlPost) ? urlPost : undefined;
    const authorId = urlAuthor && UUID_RE.test(urlAuthor) ? urlAuthor : undefined;

    // Both must be present to be valid attribution (we can't credit an
    // anonymous author or a post with no author).
    if (!postId || !authorId) return { fingerprint };

    return { postId, authorId, fingerprint };
  }, [searchParams, override?.postId, override?.authorId, fingerprint]);
}

/** Build the referral body slice the trade endpoints accept. */
export function referralBody(attrib: ReferralAttribution): {
  referralPostId?: string;
  referralAuthorId?: string;
  referralFingerprint?: string;
} {
  if (!attrib.postId || !attrib.authorId) {
    // Still send fingerprint even without attribution — harmless, and lets
    // the backend update the trader's last_fingerprint for future checks.
    return attrib.fingerprint ? { referralFingerprint: attrib.fingerprint } : {};
  }
  return {
    referralPostId: attrib.postId,
    referralAuthorId: attrib.authorId,
    referralFingerprint: attrib.fingerprint,
  };
}

/**
 * Append post-author referral query params to a URL. Centralised so every
 * share/link/clipboard call site uses the same key names — keeps URL
 * compatibility with `useReferralAttribution`'s reader and avoids drift
 * if we ever rename the params again.
 *
 * Returns the original URL unchanged when either id is missing — the
 * consumer doesn't have to guard.
 */
export function withReferralParams(
  url: string,
  postId?: string | null,
  authorId?: string | null,
): string {
  if (!postId || !authorId) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}via=${encodeURIComponent(authorId)}&p=${encodeURIComponent(postId)}`;
}
